/**
 * useRadarConfig — the Admin page's data hook.
 *
 * One industry, one snapshot. A load pulls the three list reads in
 * parallel; Confirm fans the submit plan out to the client facade one op
 * at a time, re-loads on success, and leaves `error` populated on the
 * first failure (a partial batch stops; the service has no multi-op
 * transaction, so trying to roll back would double the chance of leaving
 * surprising state behind). News-source rows go through their own
 * methods (`createNewsSource` / etc.) because the page fires them per-row
 * rather than batched through Confirm — see formState.ts.
 */

import { useCallback, useEffect, useState } from 'react';
import { radarConfigClient, type RadarConfigSnapshot } from './radarConfigClient';
import { denialKindOf, type DenialKind } from '../../utils/authorization';
import type { SubmitPlan } from './formState';
import type { RadarNewsSourceDraft } from '../../client/IvtHubClient';

export type RadarConfigLoadStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface SubmitResult {
  /** New rows → their newly minted topicIds, keyed by the plan's localKey. */
  createdTopicsByLocalKey: Record<string, string>;
  /**
   * How many plan steps actually committed on the service. A viewer's 403
   * on the first step surfaces as 0 here; the page uses this to decide
   * whether to rebuild the form from the post-reload snapshot (reflecting
   * what landed) or keep the in-progress edits intact (nothing landed, so
   * resyncing would wipe them with no gain).
   */
  stepsCommitted: number;
}

export interface RadarConfigState {
  snapshot: RadarConfigSnapshot | null;
  status: RadarConfigLoadStatus;
  error: string | null;
  denial: DenialKind | null;
  /** Re-fetch the current industry's snapshot. */
  reload: () => void;
  /** Load (or switch to) an industry — resets state. */
  loadIndustry: (industry: string) => void;
  /** Fan out a Confirm batch; throws on the first failure. */
  submit: (plan: SubmitPlan) => Promise<SubmitResult>;
  /** News-source operations — fired per-row from the page's table UI. */
  createNewsSource: (draft: RadarNewsSourceDraft) => Promise<void>;
  updateNewsSource: (sourceId: string, draft: RadarNewsSourceDraft) => Promise<void>;
  removeNewsSource: (sourceId: string) => Promise<void>;
  /** True while a submit / news-source op is in flight. */
  submitting: boolean;
  /**
   * True while a reloadKey-driven background refetch is in flight. The
   * page keeps the Form mounted (`status` stays `'ready'`) so a Confirm or
   * news-source op does not lose focus, cursor position, or scroll.
   */
  refreshing: boolean;
}

export function useRadarConfig(initialIndustry: string | null): RadarConfigState {
  const [industry, setIndustry] = useState<string | null>(initialIndustry);
  const [snapshot, setSnapshot] = useState<RadarConfigSnapshot | null>(null);
  const [status, setStatus] = useState<RadarConfigLoadStatus>(initialIndustry ? 'loading' : 'idle');
  const [error, setError] = useState<string | null>(null);
  // Two denial sources — the LOAD path (viewer missing even the read role)
  // and the WRITE path (viewer has read role but 403s on editor-only or
  // industry-scoped writes). They must track separately: a write 403
  // followed by a successful reload would otherwise wipe the hint before
  // the UI reads it, because the reload only requires the read role.
  const [readDenial, setReadDenial] = useState<DenialKind | null>(null);
  const [writeDenial, setWriteDenial] = useState<DenialKind | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // True while a reloadKey-driven background refetch is in flight. Separate
  // from `status === 'loading'` so a Confirm / news-source refresh does not
  // unmount the Form and lose focus / cursor / scroll.
  const [refreshing, setRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!industry) {
      setStatus('idle');
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    // Distinguish the FIRST load for an industry (status flips to 'loading'
    // so the page can show a spinner before any snapshot exists) from a
    // background REFRESH triggered by reloadKey (status stays 'ready' so
    // the Form does not unmount and lose transient focus / cursor state).
    const isFirstLoadForIndustry = !snapshot || snapshot.industry !== industry;
    if (isFirstLoadForIndustry) {
      setStatus('loading');
      setRefreshing(false);
    } else {
      setRefreshing(true);
    }
    setError(null);
    (async () => {
      try {
        const snap = await radarConfigClient.getSnapshot(industry);
        if (!cancelled) {
          setSnapshot(snap);
          setStatus('ready');
          setReadDenial(null);
          setRefreshing(false);
        }
      } catch (e) {
        if (cancelled) return;
        setReadDenial(denialKindOf(e));
        setError((e as Error).message ?? 'Unknown error');
        // Only flip the whole page to 'failed' on a FIRST load — a transient
        // background-refresh failure must not unmount the Form (that would
        // discard every in-progress topic / GENERAL edit, which is the
        // regression the refresh path exists to prevent). The error surfaces
        // via `error` either way.
        if (isFirstLoadForIndustry) {
          setStatus('failed');
        }
        setRefreshing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industry, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const loadIndustry = useCallback((next: string) => {
    setIndustry(next);
    setSnapshot(null);
    setError(null);
    // A real industry switch wipes both denial lanes — a different industry
    // has its own grants, so neither of the previous industry's hints apply.
    setReadDenial(null);
    setWriteDenial(null);
  }, []);

  const submit = useCallback(
    async (plan: SubmitPlan): Promise<SubmitResult> => {
      if (!industry) throw new Error('No industry selected');
      if (plan.errors.length) throw new Error(plan.errors[0]);
      setSubmitting(true);
      const createdTopicsByLocalKey: Record<string, string> = {};
      let stepsCommitted = 0;
      // Reload REGARDLESS of success/failure. A mid-batch failure still
      // leaves earlier steps on the service; a partial state the user
      // can see is strictly less confusing than a stale form that lets
      // them re-click Confirm and have the already-landed ops come back
      // as ResourceNotFound / duplicate-create. The thrown error still
      // propagates to the caller for the Alert.
      try {
        for (const step of plan.steps) {
          switch (step.kind) {
            case 'upsertGeneral':
              await radarConfigClient.upsertGeneral(industry, step.draft);
              break;
            case 'createTopic': {
              const topicId = await radarConfigClient.upsertTopic(industry, step.draft);
              createdTopicsByLocalKey[step.localKey] = topicId;
              break;
            }
            case 'updateTopic':
              await radarConfigClient.upsertTopic(industry, step.draft, step.topicId);
              break;
            case 'removeTopic':
              await radarConfigClient.setTopicStatus(industry, step.topicId, 'INACTIVE');
              break;
          }
          // Count the step as the last line of its case so a throw inside
          // the client call does NOT increment it — the caller then knows
          // nothing landed and can keep the user's in-progress edits.
          stepsCommitted += 1;
        }
        // Clear the write-denial lane on a successful submit — mirrors the
        // load path's clear on a successful read, so a later classified
        // read failure isn't shadowed by a stale write hint.
        setWriteDenial(null);
        return { createdTopicsByLocalKey, stepsCommitted };
      } catch (e) {
        // Classify the error so a viewer's 403 reads as the role/scope hint
        // and matches what the load path already surfaces. Attach the step
        // count so the caller can tell "nothing landed" (0) apart from
        // "mid-batch failure" (>0) — the page uses this to decide whether
        // to rebuild the form from the reloaded snapshot.
        setWriteDenial(denialKindOf(e));
        (e as { stepsCommitted?: number }).stepsCommitted = stepsCommitted;
        throw e;
      } finally {
        // Always re-fetch — see note above. On success this surfaces the
        // handler's new topicIds / updatedAt / displayOrder; on failure
        // it keeps the form from desynchronising with the service.
        setReloadKey((k) => k + 1);
        setSubmitting(false);
      }
    },
    [industry],
  );

  // The three per-row news-source paths all share "classify-on-throw +
  // reload-always", so a single wrapper keeps the error-classification
  // behaviour in one place.
  const runNewsSourceOp = useCallback(
    async (op: () => Promise<void>): Promise<void> => {
      setSubmitting(true);
      try {
        await op();
        setWriteDenial(null);
      } catch (e) {
        setWriteDenial(denialKindOf(e));
        throw e;
      } finally {
        setReloadKey((k) => k + 1);
        setSubmitting(false);
      }
    },
    [],
  );

  const createNewsSource = useCallback(
    async (draft: RadarNewsSourceDraft) => {
      if (!industry) throw new Error('No industry selected');
      await runNewsSourceOp(() => radarConfigClient.upsertNewsSource(industry, draft).then());
    },
    [industry, runNewsSourceOp],
  );

  const updateNewsSource = useCallback(
    async (sourceId: string, draft: RadarNewsSourceDraft) => {
      if (!industry) throw new Error('No industry selected');
      await runNewsSourceOp(() =>
        radarConfigClient.upsertNewsSource(industry, draft, sourceId).then(),
      );
    },
    [industry, runNewsSourceOp],
  );

  const removeNewsSource = useCallback(
    async (sourceId: string) => {
      if (!industry) throw new Error('No industry selected');
      await runNewsSourceOp(() =>
        radarConfigClient.setNewsSourceStatus(industry, sourceId, 'INACTIVE'),
      );
    },
    [industry, runNewsSourceOp],
  );

  // Write denial takes precedence when both are set — a user who successfully
  // read the snapshot but hit a write 403 cares about the role/scope hint,
  // not an obsolete load-time denial from the previous industry.
  const denial: DenialKind | null = writeDenial ?? readDenial;

  return {
    snapshot,
    status,
    error,
    denial,
    reload,
    loadIndustry,
    submit,
    createNewsSource,
    updateNewsSource,
    removeNewsSource,
    submitting,
    refreshing,
  };
}

/** Pick the max updatedAt across the three shapes for the page's audit badge. */
export function pickLastUpdated(
  snap: RadarConfigSnapshot | null,
): { by?: string; at?: Date } | null {
  if (!snap) return null;
  const stamps: Array<{ by?: string; at?: Date }> = [];
  if (snap.general) stamps.push({ by: snap.general.updatedBy, at: snap.general.updatedAt });
  for (const t of snap.topics) stamps.push({ by: t.updatedBy, at: t.updatedAt });
  for (const s of snap.newsSources) stamps.push({ by: s.updatedBy, at: s.updatedAt });
  const latest = stamps
    .filter((s): s is { by?: string; at: Date } => s.at instanceof Date)
    .sort((a, b) => b.at.getTime() - a.at.getTime())[0];
  return latest ?? null;
}

